/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { FaAngleRight } from "react-icons/fa";
import { useParams } from "react-router";
import { FcGoogle } from "react-icons/fc";

import {
  emptyString,
  screenResize,
  questionsJson,
  loginConstants,
  questionTypeConstants,
  surveyQuestionsConstants,
  fiveStar,
  screenSize,
  copyStatus,
  fourStar,
  NPS_FIRST_QUESTION_ORDER,
  customerSurveyConstants,
  customerSurveyLabels,
  EMPTY_ARRAY_LENGTH,
} from "../../constants";
import { useSpinner, onKeyDownHandler, Spinner } from "../../../../shared";
import {
  getItemFromStorage,
  SURVEY_INTERACTIONS_TYPE_ENUM,
} from "../../../../system";
import { customerSurveyActions } from "../../redux/actions";

import checkMark from "../../../../assets/images/check-mark.gif";
import SurveyQuestionRenderer from "./customerQuestionType/shared/surveyQuestionRenderer";
import SurveyLogoBanner from "./customerQuestionType/shared/surveyLogoBanner";
import SurveyStatusMessage from "./customerQuestionType/shared/surveyStatusMessage";
import QuestionHeadingLogo from "./customerQuestionType/shared/questionHeadingLogo";
import UnsupportedQuestionType from "./customerQuestionType/shared/unsupportedQuestionType";
import { applySurveyThemeStyles } from "./utils/applySurveyThemeStyles";

const { MOBILE } = screenSize;
const {
  DEFAULT_QUESTION_PAGE,
  ZERO_ORDER_ID,
  MIN_QUESTIONS_TO_RENDER,
  GOOGLE_ICON_SIZE,
  ANGLE_ICON_SIZE,
} = customerSurveyConstants;

const CustomerSurvey = (props) => {
  const {
    customerSurveyReducer: {
      surveyTheme,
      brandDetail,
      surveyId,
      customerDetail,
      questionsArray,
      allResponsesArray,
      isSurveySubmitted,
      isAllowedToViewSurvey,
      isCustomerSurveySubmitted,
    },
    addSurveyResponse,
    getThemeByBrandId,
    addInvalidOrderInfo,
    getBrandDetailByBrandId,
    getSurveyDataByEncryptedParams,
    logSurveyInteractionEvent,
  } = props;

  const { googleSocialLink } = customerDetail || {};
  const { mainThemeColor, fontColor, progressBarColor, fontStyle } =
    surveyTheme || {};

  const { encryptedParams } = useParams();
  const brandId = getItemFromStorage(loginConstants.BRAND_ID);

  // Initialize states.
  const [currentQuestion, setCurrentQuestion] = useState(
    DEFAULT_QUESTION_PAGE,
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [customerQuestions, setCustomerQuestions] = useState(questionsJson);
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Loading state
  const [hasMovedToSecondQuestion, setHasMovedToSecondQuestion] =
    useState(false);
  const [commentCopied, setCommentCopied] = useState({
    status: false,
    comment: emptyString,
  });
  const [isNPSFirstActive, setIsNPSFirstActive] = useState(false);
  const [isFiveStarOnNPS, setIsFiveStarOnNPS] = useState(false);

  const hasTrackedPageViewRef = useRef(false);

  const lastTextBoxQuestion =
    Array.isArray(customerQuestions) &&
    customerQuestions.length > EMPTY_ARRAY_LENGTH
      ? customerQuestions[customerQuestions.length - 1]
      : null;

  // Show loading while a request is running.
  const spinner = useSpinner();

  // Function to handle back button.
  const handleBackButton = () => {
    setCurrentQuestion((prev) => (prev > DEFAULT_QUESTION_PAGE ? prev - 1 : prev));
  };

  const handleSurveyInteractionEvent = (eventType) => {
    const dataToSend = {
      eventType: eventType,
      orderId: customerDetail?.orderId,
      customerEventId: customerDetail?.customerEventId,
    };

    logSurveyInteractionEvent(dataToSend);
  };

  // Function to handle next button.
  const handleNextButton = () => {
    // Check if the current question is the default page
    if (
      currentQuestion === DEFAULT_QUESTION_PAGE &&
      !hasMovedToSecondQuestion
    ) {
      handleSurveyInteractionEvent(
        SURVEY_INTERACTIONS_TYPE_ENUM.SURVEY_NEXT_QUESTION,
      );
      setHasMovedToSecondQuestion(true); // Mark the event as handled
    }

    setCurrentQuestion((prev) =>
      prev + 1 < (customerQuestions?.length || 0) ? prev + 1 : prev,
    );
  };

  // Function to submit survey response.
  const handleSubmit = (data) => {
    const body = {
      surveyId: surveyId,
      orderId: customerDetail.orderId,
      customerId: customerDetail.customerId,
      customerEventId: customerDetail.customerEventId,
      response: data.flat(1) || customerQuestions.flat(1),
      allResponsesJson: allResponsesArray,
    };

    addSurveyResponse(body, brandDetail);
  };

  //Copy comment to clipboard
  const copyCommentsToClipboard = (text) => {
    navigator?.clipboard
      ?.writeText(text)
      .then(() => setCommentCopied({ comment: text, status: true }));
  };

  //Check if all star rating question answers are equal to 5
  const isAllStarRatingAnswersPositive = () => {
    const allStarRatingQuestions = (customerQuestions || []).filter(
      (ques) =>
        Number(ques.questionTypeId) === questionTypeConstants.STAR_RANKING_ID,
    );
    return allStarRatingQuestions.every((ques) => ques.answer === fiveStar);
  };

  //Check if NPS First is active and first question (NPS question) has 4 or 5-star answer
  const isNPSFirstWithFiveStar = () => {
    if (!isNPSFirstActive) return false;

    //Find the NPS question (questionOrder === -1)
    const npsQuestion = (customerQuestions || []).find(
      (q) => q.questionOrder === NPS_FIRST_QUESTION_ORDER,
    );
    return npsQuestion?.answer === fiveStar || npsQuestion?.answer === fourStar;
  };

  //Check if should show Google review (either all 5 stars OR NPS First with 4 or 5 stars on NPS question)
  const shouldShowGoogleReview = () => {
    if (isNPSFirstActive) {
      return isNPSFirstWithFiveStar();
    }
    return isAllStarRatingAnswersPositive();
  };

  //Function to check if user gave 4 or 5 stars on NPS question
  const checkFiveStarOnNPS = (rating) => {
    const currentQuestionData = customerQuestions?.[currentQuestion];

    setIsFiveStarOnNPS(
      isNPSFirstActive &&
        currentQuestionData?.questionOrder === NPS_FIRST_QUESTION_ORDER &&
        (rating === Number(fiveStar) || rating === Number(fourStar)),
    );
  };

  // Getting window innerwidth for removing and adding logo background.
  useEffect(() => {
    // Getting survey and customer detail when page rendered.
    getSurveyDataByEncryptedParams(encryptedParams);

    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };
    // Add event listener for screen resize.
    window.addEventListener(screenResize, handleWindowResize);

    // Remove event listener for screen resize.
    return () => {
      window.removeEventListener(screenResize, handleWindowResize);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (customerDetail?.orderId === ZERO_ORDER_ID) {
      let obj = {
        customerEventId: customerDetail.customerEventId,
      };
      addInvalidOrderInfo(obj);
    }
    if (
      customerDetail &&
      Object.keys(customerDetail).length !== EMPTY_ARRAY_LENGTH &&
      customerDetail?.orderId !== ZERO_ORDER_ID &&
      !isSurveySubmitted &&
      !hasTrackedPageViewRef.current
    ) {
      hasTrackedPageViewRef.current = true;
    }

    // eslint-disable-next-line
  }, [customerDetail?.orderId]);

  // Set customer questions.
  useEffect(() => {
    if (
      Array.isArray(questionsArray) &&
      questionsArray.length > EMPTY_ARRAY_LENGTH
    ) {
      // Check if NPS First is active (questionOrder === -1)
      const npsQuestion = questionsArray.find(
        (q) => q.questionOrder === NPS_FIRST_QUESTION_ORDER,
      );
      setIsNPSFirstActive(!!npsQuestion);

      setCustomerQuestions(questionsArray);
      setIsDataLoaded(true);
    }
    // eslint-disable-next-line
  }, [questionsArray]);

  // Getting brand detail and survey theme for customer survey.
  useEffect(() => {
    if (brandId) {
      getThemeByBrandId(brandId);
      getBrandDetailByBrandId(brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Updating theme for customer survey according to brandId.
  useEffect(() => {
    applySurveyThemeStyles({
      mainThemeColor,
      fontColor,
      fontStyle,
      progressBarColor,
    });
  }, [mainThemeColor, fontColor, fontStyle, progressBarColor]);

  useEffect(() => {
    if (!isDataLoaded || !customerDetail) return;

    let surveyInteractionType;
    if (isSurveySubmitted) {
      surveyInteractionType =
        SURVEY_INTERACTIONS_TYPE_ENUM.SURVEY_ALREADY_SUBMITTED;
    } else if (customerDetail.orderId === ZERO_ORDER_ID) {
      surveyInteractionType = SURVEY_INTERACTIONS_TYPE_ENUM.SURVEY_LINK_EXPIRED;
    } else {
      surveyInteractionType = SURVEY_INTERACTIONS_TYPE_ENUM.SURVEY_ACCESS;
    }

    handleSurveyInteractionEvent(surveyInteractionType);
  }, [isDataLoaded, customerDetail, isSurveySubmitted]);

  useEffect(() => {
    if (isCustomerSurveySubmitted) {
      if (shouldShowGoogleReview() && googleSocialLink) {
        handleSurveyInteractionEvent(
          SURVEY_INTERACTIONS_TYPE_ENUM.RECEIVED_GOOGLE_LINK,
        );
      }
      handleSurveyInteractionEvent(
        SURVEY_INTERACTIONS_TYPE_ENUM.SURVEY_SUBMISSION,
      );
    }
  }, [isCustomerSurveySubmitted]);

  const renderCustomerSurveyContent = () => {
    if (isSurveySubmitted) {
      return renderAlreadySubmittedSurvey();
    }
    if (
      customerDetail?.orderId === ZERO_ORDER_ID ||
      !isAllowedToViewSurvey
    ) {
      return renderExpiredSurvey();
    }
    if (isCustomerSurveySubmitted) {
      return renderSubmittedSurvey();
    }
    if (
      Array.isArray(customerQuestions) &&
      customerQuestions.length > MIN_QUESTIONS_TO_RENDER
    ) {
      return renderSurveyQuestions();
    }
    return renderNoSurvey();
  };

  const renderExpiredSurvey = () => (
    <div className="d-flex flex-column align-items-center question-container">
      <SurveyLogoBanner brandDetail={brandDetail} windowWidth={windowWidth} />
      <div className="my-auto">
        <h2 className="fw-bold text-center">
          {customerSurveyLabels.SURVEY_LINK_EXPIRED}
        </h2>
      </div>
    </div>
  );

  const renderAlreadySubmittedSurvey = () => (
    <SurveyStatusMessage
      title={customerSurveyLabels.SURVEY_ALREADY_SUBMITTED}
      showHeadingLogo
      brandDetail={brandDetail}
    />
  );

  const renderSubmittedSurvey = () => (
    <>
      <QuestionHeadingLogo brandDetail={brandDetail} />
      <div className="d-flex flex-column align-items-center w-100">
        {shouldShowGoogleReview() && googleSocialLink
          ? renderGoogleReviewSection()
          : renderSimpleSubmittedMessage()}
      </div>
    </>
  );

  const renderGoogleReviewSection = () => (
    <div
      className={`my-auto  form-section-wrapper col-md-10 mx-auto m-auto px-md-5  ${
        windowWidth > MOBILE ? "w-60" : "w-90"
      } `}
    >
      <div className="d-flex align-items-center justify-content-center">
        <div className="col-12 text-center">
          <img src={checkMark} alt={customerSurveyLabels.LOGO_ALT} />
        </div>
      </div>
      <h4 className="fw-bold text-center text-black mb-0">
        {customerSurveyLabels.SURVEY_SUBMITTED_SHORT}
      </h4>
      <div>
        <div>
          <p className="share-rating-message">
            <strong> {customerSurveyLabels.ENJOYED_EXPERIENCE_STRONG}</strong>{" "}
            {customerSurveyLabels.GOOGLE_REVIEW_HELP}
          </p>
          <div className="d-flex align-items-center text-center mt-2">
            <hr className="flex-grow-1" />
            <h6 className="px-2 fw-semibold mb-0">
              {customerSurveyLabels.WANT_TO_HELP}
            </h6>
            <hr className="flex-grow-1" />
          </div>
          <div className="d-flex align-items-center justify-content-center">
            <a
              href={googleSocialLink}
              className="mt-1 google-review-button w-100 text-decoration-none rounded border border-1"
              onContextMenu={(e) => e.preventDefault()} // This disables right-click
            >
              <div
                className="mx-auto shadow d-flex justify-content-center google-review-content align-items-center gap-2"
                type="submit"
                role="button"
                tabIndex={0}
                onClick={() => {
                  submitGoogleReview();
                }}
                onKeyDown={(e) => {
                  onKeyDownHandler(e, () => {
                    submitGoogleReview();
                  });
                }}
              >
                <div className="google-logo-wrapper bg-white rounded-circle">
                  <FcGoogle size={GOOGLE_ICON_SIZE} />
                </div>

                <h6 className="text-white mb-0 rounded-end fw-semibold">
                  {customerSurveyLabels.LEAVE_GOOGLE_REVIEW}
                </h6>
                <FaAngleRight color="white" size={ANGLE_ICON_SIZE} />
              </div>
            </a>
          </div>
          <p className="share-rating-message">
            {customerSurveyLabels.TAKES_LESS_THAN_30}
          </p>
          {lastTextBoxQuestion?.answer && renderAutoCopiedComment()}
        </div>
      </div>
    </div>
  );

  const submitGoogleReview = () => {
    handleSurveyInteractionEvent(SURVEY_INTERACTIONS_TYPE_ENUM.GOOGLE_REVIEW);
  };

  /* prettier-ignore */
  const renderAutoCopiedComment = () => (
    <>
      <div className="text-center mb-1">
        <small className="text-center clipboard-copy-info-text fw-normal d-flex flex-column">
          <span>
            {customerSurveyLabels.AUTO_COPIED_COMMENT_PREFIX}{" "}
            <span className="fw-bold">{customerSurveyLabels.AUTO_COPIED_BOLD}</span>{" "}
            {customerSurveyLabels.AUTO_COPIED_COMMENT_MIDDLE}{" "}
            <span className="fw-bold">{customerSurveyLabels.PASTE_BOLD}</span>{" "}
            {customerSurveyLabels.AUTO_COPIED_COMMENT_SUFFIX}
          </span>
          <span>{customerSurveyLabels.MISSED_COPY_HINT}</span>
        </small>
      </div>
      <div className="py-2 w-100">
        <div className="d-flex align-items-stretch  w-100 rounded comment-area">
          <div className="align-self-center green-color flex-fill p-2 d-flex align-items-center justify-content-start h-100">
            <p className="mb-0 px-2 clipboard-copy-message fw-normal">
              <i>{lastTextBoxQuestion?.answer}</i>
            </p>
          </div>
          <div
            role="button"
            tabIndex={0}
            className={`clipboard-btn bg-opacity-75 border ${commentCopied.comment === lastTextBoxQuestion?.answer
              ? "clipboard-btn-light-green-bg text-white"
              : "clipboard-btn bg-opacity-10"
              } rounded p-3 align-self-center`}
            onClick={() => {
              copyCommentsToClipboard(lastTextBoxQuestion?.answer);
            }}
            onKeyDown={(e) => {
              onKeyDownHandler(e, () => {
                copyCommentsToClipboard(lastTextBoxQuestion?.answer);
              });
            }}
          >
            {commentCopied.comment === lastTextBoxQuestion?.answer
              ? copyStatus.COPIED
              : copyStatus.COPY}
          </div>
        </div>
      </div>
      {commentCopied.status && (
        <p className="text-center clipboard-copy-info-text mb-1">
          {customerSurveyLabels.COMMENT_COPIED_CONFIRMATION}
        </p>
      )}
    </>
  );

  const renderSimpleSubmittedMessage = () => (
    <SurveyStatusMessage
      title={customerSurveyLabels.SURVEY_SUBMITTED}
      showCheckIcon
      containerClassName="my-auto"
      titleClassName="fw-bolder text-center text-black"
    />
  );

  const renderSurveyQuestions = () => {
    const currentQuestionData = customerQuestions?.[currentQuestion];

    if (!currentQuestionData) {
      return <UnsupportedQuestionType />;
    }

    return (
      <SurveyQuestionRenderer
        question={currentQuestionData}
        brandDetail={brandDetail}
        surveyTheme={surveyTheme || {}}
        questionArray={customerQuestions}
        customerDetail={customerDetail}
        currentQuestion={currentQuestion}
        handleNextButton={handleNextButton}
        handleBackButton={handleBackButton}
        setCustomerQuestions={setCustomerQuestions}
        handleSubmitSurvey={handleSubmit}
        isNPSFirstActive={isNPSFirstActive}
        isFiveStarOnNPS={isFiveStarOnNPS}
        checkFiveStarOnNPS={checkFiveStarOnNPS}
        handleSurveyInteractionEvent={handleSurveyInteractionEvent}
        isAllStarRatingAnswersPositive={isAllStarRatingAnswersPositive}
      />
    );
  };

  const renderNoSurvey = () => (
    <div className="d-flex flex-column align-items-center question-container">
      <SurveyLogoBanner brandDetail={brandDetail} windowWidth={windowWidth} />
      <div
        className={`my-auto ${
          !customerQuestions?.length ? emptyString : "d-none"
        }`}
      >
        <h2 className="fw-bold text-center">
          {customerSurveyLabels.NO_SURVEY_EXIST}
        </h2>
      </div>
    </div>
  );

  return (
    <>
      {spinner(surveyQuestionsConstants.SURVEY_SCREEN_SPINNER) ||
      spinner(surveyQuestionsConstants.GET_QUESTIONS_ARRAY) ||
      spinner(surveyQuestionsConstants.GET_BRAND_DETAIL_BY_ID) ? (
        <Spinner />
      ) : (
        renderCustomerSurveyContent()
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  customerSurveyReducer: state.customerSurveyReducer,
});

const mapDispatchToProps = (dispatch) => ({
  addSurveyResponse: (body, brandDetail) =>
    dispatch(customerSurveyActions.addSurveyResponse(body, brandDetail)),
  logSurveyInteractionEvent: (body) =>
    dispatch(customerSurveyActions.logSurveyInteractionEvent(body)),
  getThemeByBrandId: (brandId) =>
    dispatch(customerSurveyActions.getThemeByBrandId(brandId)),
  getBrandDetailByBrandId: (brandId) =>
    dispatch(customerSurveyActions.getBrandDetailByBrandId(brandId)),
  getSurveyDataByEncryptedParams: (encryptedData) =>
    dispatch(
      customerSurveyActions.getSurveyDataByEncryptedParams(encryptedData),
    ),
  addInvalidOrderInfo: (body) =>
    dispatch(customerSurveyActions.addInvalidOrderInfo(body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerSurvey);
